import React, { useState } from 'react';
import { Box, Typography, Container, Input, Button, FormControl, FormLabel } from '@mui/joy';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { setToken } from './adminApi';
import { Logo } from './Logo';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Invalid email or password');
      }
      const data = await response.json();
      setToken(data.access_token);
      toast.success('Logged in');
      navigate('/admin/dashboard');
    } catch (err) {
      toast.error(err.message || 'Login failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
      <Container maxWidth="sm">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box
            sx={{
              p: { xs: 4, md: 6 },
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4, justifyContent: 'center' }}>
              <Logo size={40} />
              <Typography level="h3" sx={{ fontWeight: 700, color: '#ffffff' }}>
                Admin
              </Typography>
            </Box>

            <Typography
              level="h4"
              sx={{
                textAlign: 'center',
                mb: 4,
                fontWeight: 600,
                background: 'linear-gradient(135deg, #1B365D 0%, #2E5BBA 50%, #00A86B 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Sign in to manage content
            </Typography>

            <form onSubmit={handleSubmit}>
              <FormControl sx={{ mb: 3 }} required>
                <FormLabel sx={{ color: 'text.secondary', fontWeight: 600 }}>Email</FormLabel>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@calvetsolutions.com"
                  autoComplete="username"
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    '&:focus-within': {
                      border: '1px solid #2E5BBA',
                    },
                  }}
                />
              </FormControl>

              <FormControl sx={{ mb: 4 }} required>
                <FormLabel sx={{ color: 'text.secondary', fontWeight: 600 }}>Password</FormLabel>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    '&:focus-within': {
                      border: '1px solid #2E5BBA',
                    },
                  }}
                />
              </FormControl>

              <Button
                type="submit"
                size="lg"
                loading={isSubmitting}
                sx={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #2E5BBA 0%, #00A86B 100%)',
                  color: 'white',
                  fontWeight: 600,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1B365D 0%, #2E5BBA 100%)',
                  },
                }}
              >
                Sign In
              </Button>
            </form>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Link to="/" style={{ textDecoration: 'none' }}>
              <Typography level="body2" sx={{ color: 'text.secondary', '&:hover': { color: '#2E5BBA' } }}>
                ← Back to site
              </Typography>
            </Link>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default AdminLoginPage;
